import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  MAIL_RECEIVE_AUTHS,
  MAIL_RECEIVE_AUTH_LABELS,
  formatDateTime,
  messages,
  type IntegrationStatus,
  type MailReceiveAuth,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/common/page';
import { cn } from '@/lib/utils';

type ReceiveConfig = {
  auth: MailReceiveAuth;
  user: string;
  host: string;
  port: number;
  mailbox: string;
  since: string;
};

const EMPTY: ReceiveConfig = {
  auth: 'google_oauth',
  user: '',
  host: '',
  port: 993,
  mailbox: 'INBOX',
  since: '',
};

/** S78 メール受信（admin のみ）。返信を取り込む受信箱の設定 */
export const SettingsMailReceivePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<ReceiveConfig>(EMPTY);
  const [serviceAccountKey, setServiceAccountKey] = useState('');
  const [imapPassword, setImapPassword] = useState('');

  const { data: integrations, isLoading } = useQuery({
    queryKey: qk.integrations,
    queryFn: () => api.get<SingleResponse<IntegrationStatus[]>>('/settings/integrations'),
  });
  const receive = integrations?.data.find((i) => i.provider === 'mail_receive');

  useEffect(() => {
    if (!receive) return;
    const stored = receive.config as Partial<ReceiveConfig>;
    setConfig({ ...EMPTY, ...stored });
  }, [receive]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/settings/integrations/mail_receive', {
        ...(serviceAccountKey.trim() ? { apiKey: serviceAccountKey.trim() } : {}),
        config: {
          ...config,
          port: Number(config.port) || 993,
          ...(imapPassword ? { imapPassword } : {}),
        },
      }),
    onSuccess: () => {
      toast.success(messages.common.saved);
      setServiceAccountKey('');
      setImapPassword('');
      void queryClient.invalidateQueries({ queryKey: qk.integrations });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ ok: boolean; message: string }>>('/settings/integrations/mail_receive/test'),
    onSuccess: (result) => {
      if (result.data.ok) {
        toast.success(messages.settings.testSucceeded, { description: result.data.message });
      } else {
        toast.error(messages.settings.testFailed, { description: result.data.message });
      }
      void queryClient.invalidateQueries({ queryKey: qk.integrations });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const fetchNow = useMutation({
    mutationFn: () => api.post('/inbox/sync'),
    onSuccess: () => toast.success(messages.settings.fetchStarted),
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (isLoading) return <SkeletonRows rows={5} />;

  const isGoogle = config.auth === 'google_oauth';

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.mailReceive}
        description={messages.settings.mailReceiveNote}
      />

      <div className="space-y-4">
        {isGoogle && (
          <Card>
            <CardHeader>
              <CardTitle>Google Workspace での準備</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-2 text-note text-foreground-muted">
                Google Workspace はパスワードでの IMAP サインインを終了しているため、サービスアカウントで受信箱を読みます。人の同意画面は要りません。
              </p>
              <ol className="list-decimal space-y-1 pl-5 text-body">
                {messages.settings.googleWorkspaceSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox aria-hidden className="size-5" />
              受信箱
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label={messages.settings.receiveAuth} required>
              {(props) => (
                <Select
                  {...props}
                  value={config.auth}
                  onChange={(e) => setConfig((p) => ({ ...p, auth: e.target.value as MailReceiveAuth }))}
                >
                  {MAIL_RECEIVE_AUTHS.map((auth) => (
                    <option key={auth} value={auth}>
                      {MAIL_RECEIVE_AUTH_LABELS[auth]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label={messages.settings.receiveUser}
              required
              hint={isGoogle ? messages.settings.receiveUserNote : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  autoComplete="off"
                  value={config.user}
                  onChange={(e) => setConfig((p) => ({ ...p, user: e.target.value }))}
                  placeholder="sales-inbox@example.co.jp"
                />
              )}
            </Field>

            {isGoogle ? (
              <Field
                label={messages.settings.serviceAccountKey}
                required={!receive?.maskedKey}
                hint={
                  receive?.maskedKey
                    ? `${messages.settings.serviceAccountRegistered(receive.maskedKey)}。差し替える場合だけ新しい鍵を貼り付けてください`
                    : messages.settings.serviceAccountKeyNote
                }
              >
                {(props) => (
                  <Textarea
                    {...props}
                    rows={6}
                    autoComplete="off"
                    spellCheck={false}
                    value={serviceAccountKey}
                    onChange={(e) => setServiceAccountKey(e.target.value)}
                    placeholder='{"type": "service_account", "client_email": "...", "private_key": "..."}'
                    className="font-mono text-note"
                  />
                )}
              </Field>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label={messages.settings.imapHost} required>
                  {(props) => (
                    <Input
                      {...props}
                      value={config.host}
                      onChange={(e) => setConfig((p) => ({ ...p, host: e.target.value }))}
                      placeholder="imap.example.co.jp"
                    />
                  )}
                </Field>
                <Field label={messages.settings.imapPort} hint="993 なら SSL/TLS で接続します">
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      value={config.port}
                      onChange={(e) => setConfig((p) => ({ ...p, port: Number(e.target.value) }))}
                    />
                  )}
                </Field>
                <Field label={messages.settings.imapPassword} hint={messages.settings.keyMasked}>
                  {(props) => (
                    <Input
                      {...props}
                      type="password"
                      autoComplete="off"
                      value={imapPassword}
                      onChange={(e) => setImapPassword(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <Field label={messages.settings.mailbox} hint={messages.settings.mailboxNote}>
                {(props) => (
                  <Input
                    {...props}
                    value={config.mailbox}
                    onChange={(e) => setConfig((p) => ({ ...p, mailbox: e.target.value }))}
                  />
                )}
              </Field>
              <Field label={messages.settings.receiveSince} hint={messages.settings.receiveSinceNote}>
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={config.since}
                    onChange={(e) => setConfig((p) => ({ ...p, since: e.target.value }))}
                  />
                )}
              </Field>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>接続の確認</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-note text-foreground-muted">
              保存してから接続をテストします。失敗したときは、直す場所がメッセージに出ます。
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !receive?.configured}>
                {messages.settings.testConnection}
              </Button>
              <Button
                variant="outline"
                onClick={() => fetchNow.mutate()}
                disabled={fetchNow.isPending || !receive?.configured}
              >
                <RefreshCw aria-hidden className={cn('size-4', fetchNow.isPending && 'animate-spin')} />
                {messages.settings.fetchNow}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/inbox')}>
                受信画面を開く
              </Button>
            </div>
            {receive?.lastTestedAt && (
              <p className="text-note text-foreground-muted">
                {messages.settings.lastTestedAt}{' '}
                <span className="tabular">{formatDateTime(receive.lastTestedAt)}</span>
                {receive.lastTestResult === 'success' ? '（成功）' : '（失敗）'}
                {receive.lastTestMessage && <>：{receive.lastTestMessage}</>}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {messages.common.save}
          </Button>
        </div>
      </div>
    </>
  );
};
